import Swal from 'sweetalert2';
import { HEXAGON_LOGO_MARK_PATHS } from './hexagon-logo-mark';

const HEXAGON_LOGO_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 484 427" style="width: 60px; height: 53px;">${HEXAGON_LOGO_MARK_PATHS}</svg>`;

export interface ConfirmDialogOptions {
    title?: string;
    description?: string;
    confirmButtonText?: string;
    cancelButtonText?: string;
    confirmButtonClass?: string;
    cancelButtonClass?: string;
    icon?: 'question' | 'warning' | 'info' | 'error' | 'success';
    showIcon?: boolean;
    showLogo?: boolean;
    allowOutsideClick?: boolean;
    /** Set to false when dismissing must not be read as picking the cancel option. */
    allowEscapeKey?: boolean;
    reverseButtons?: boolean;
}

/**
 * Shows a confirmation dialog with consistent styling
 * @param options Configuration options for the dialog
 * @returns Promise that resolves to true if confirmed, false if cancelled
 */
export async function confirmDialog(options: ConfirmDialogOptions): Promise<boolean> {
    const {
        title = 'Confirmation Required',
        description,
        confirmButtonText = 'Yes',
        cancelButtonText = 'No',
        confirmButtonClass,
        cancelButtonClass = 'btn-secondary',
        icon = 'question',
        showIcon = false,
        showLogo = false,
        allowOutsideClick = false,
        allowEscapeKey = true,
        reverseButtons = true
    } = options;

    const html = description ? `${description}` : undefined;

    const isDestructive = /^(delete|remove)$/i.test(confirmButtonText.trim());

    // Determine classes
    const finalConfirmClass = confirmButtonClass ? confirmButtonClass : (isDestructive ? 'btn-danger' : 'btn-primary');

    const swalOptions: any = {
        title,
        html,
        showCancelButton: true,
        confirmButtonText,
        cancelButtonText,
        reverseButtons,
        focusCancel: false,
        allowOutsideClick,
        allowEscapeKey,
        customClass: {
            confirmButton: `btn ${finalConfirmClass}`,
            cancelButton: `btn ${cancelButtonClass}`,
            ...(showLogo ? { icon: 'swal-hexagon-logo' } : {})
        },
        buttonsStyling: false
    };

    if (showLogo) {
        swalOptions.icon = 'info';
        swalOptions.iconHtml = HEXAGON_LOGO_SVG;
    } else if (showIcon) {
        swalOptions.icon = icon;
    }

    const result = await Swal.fire(swalOptions);
    return result.isConfirmed;
}

export interface ConfirmAndOpenExternalUrlOptions {
    url: string;
    title?: string;
    /** Short description of what's being opened, e.g. "GoLink 123" — rendered before the URL. */
    urlLabel?: string;
    /** Where the link redirects to, if different from `url` itself (e.g. an affiliate link) — shown as an extra line. */
    redirectsTo?: string;
    confirmButtonText?: string;
    cancelButtonText?: string;
}

/**
 * Confirms with the user before opening an external URL in a new tab — shared by GoLinks and
 * the Brand Reviews "Path" link, both of which redirect to operator-controlled sites.
 * @returns Promise that resolves to true if the user confirmed (and the URL was opened), false if cancelled
 */
export async function confirmAndOpenExternalUrl(options: ConfirmAndOpenExternalUrlOptions): Promise<boolean> {
    const {
        url,
        title = 'Open link',
        urlLabel,
        redirectsTo,
        confirmButtonText = 'Yes',
        cancelButtonText = 'No',
    } = options;

    let description = urlLabel
        ? `You're about to open <strong>${urlLabel}</strong>:<br><span class="text-muted">${url}</span>`
        : `You're about to open:<br><span class="text-muted">${url}</span>`;

    if (redirectsTo) {
        description += `<br><br>It will redirect you to:<br><strong>${redirectsTo}</strong>`;
    }

    const confirmed = await confirmDialog({title, description, confirmButtonText, cancelButtonText});

    if (confirmed) {
        window.open(url, '_blank');
    }

    return confirmed;
}

export interface InfoDialogOptions {
    title?: string;
    description?: string;
    closeButtonText?: string;
    icon?: 'question' | 'warning' | 'info' | 'error' | 'success';
    showIcon?: boolean;
    showLogo?: boolean;
    allowOutsideClick?: boolean;
}

/**
 * Shows an informational dialog with a single dismiss button
 * @param options Configuration options for the dialog
 * @returns Promise that resolves when the dialog is dismissed
 */
export function infoDialog(options: InfoDialogOptions): Promise<void> {
    const {
        title = 'Information',
        description,
        closeButtonText = 'Got it',
        icon = 'info',
        showIcon = true,
        showLogo = false,
        allowOutsideClick = true,
    } = options;

    const html = description ? `${description}` : undefined;

    const swalOptions: any = {
        title,
        html,
        showCancelButton: false,
        confirmButtonText: closeButtonText,
        allowOutsideClick,
        customClass: {
            confirmButton: 'btn btn-primary',
            ...(showLogo ? { icon: 'swal-hexagon-logo' } : {})
        },
        buttonsStyling: false
    };

    if (showLogo) {
        swalOptions.icon = 'info';
        swalOptions.iconHtml = HEXAGON_LOGO_SVG;
    } else if (showIcon) {
        swalOptions.icon = icon;
    }

    return Swal.fire(swalOptions).then(() => undefined);
}

export interface InputDialogOptions extends ConfirmDialogOptions {
    input?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'range' | 'textarea' | 'select' | 'radio' | 'checkbox' | 'file' | 'url';
    inputLabel?: string;
    inputValue?: string;
    inputPlaceholder?: string;
    inputValidator?: (value: string) => string | null | Promise<string | null>;
}

export interface PromptDialogOptions {
    title?: string;
    description?: string;
    inputPlaceholder?: string;
    confirmButtonText?: string;
    cancelButtonText?: string;
    confirmButtonClass?: string;
}

/**
 * Shows an input dialog
 * @param options Configuration options for the dialog
 * @returns Promise that resolves to the input value if confirmed, false if cancelled
 */
export function inputDialog(options: InputDialogOptions): Promise<string | false> {
    const {
        title = 'Input Required',
        input = 'text',
        inputLabel,
        inputValue = '',
        inputPlaceholder = '',
        confirmButtonText = 'Save',
        cancelButtonText = 'Cancel',
        confirmButtonClass = 'btn-primary',
        cancelButtonClass = 'btn-secondary',
        allowOutsideClick = false,
        inputValidator,
        description
    } = options;

    const swalOptions: any = {
        title,
        // HTML, like confirmDialog's description: callers escape any server/remote text in it.
        html: description ? `${description}` : undefined,
        input,
        inputLabel,
        inputValue,
        inputPlaceholder,
        showCancelButton: true,
        confirmButtonText,
        cancelButtonText,
        reverseButtons: true,
        allowOutsideClick,
        customClass: {
            confirmButton: `btn ${confirmButtonClass}`,
            cancelButton: `btn ${cancelButtonClass}`,
            input: 'form-control mx-0 w-100',
            textarea: 'form-control mx-0 w-100'
        },
        buttonsStyling: false,
        inputValidator
    };

    return Swal.fire(swalOptions).then((result) => {
        if (result.isConfirmed) {
            return result.value || ''; // Return empty string if value is null/undefined but confirmed
        }
        return false;
    });
}


export interface OptionsDialogOptions extends ConfirmDialogOptions {
    text?: string;
    options: Record<string, string>;
    input?: 'radio' | 'select';
    inputValue?: string;
    skipDialog?: boolean;
}

/**
 * Shows a dialog to choose between multiple options
 * @param options Configuration options for the dialog
 * @returns Promise that resolves to the selected value if confirmed, false if cancelled
 */
export function optionsDialog(options: OptionsDialogOptions): Promise<string | false> {
    const {
        title = 'Choose an option',
        text,
        input = 'radio',
        options: inputOptions,
        inputValue,
        confirmButtonText = 'Select',
        cancelButtonText = 'Cancel',
        confirmButtonClass = 'btn-primary',
        cancelButtonClass = 'btn-secondary',
        allowOutsideClick = false,
        skipDialog = false
    } = options;

    // Logic to skip dialog and return default/first option
    if (skipDialog) {
        if (inputValue && inputOptions[inputValue]) {
            return Promise.resolve(inputValue);
        }
        const firstValue = Object.keys(inputOptions)[0];
        if (firstValue) {
            return Promise.resolve(firstValue);
        }
        return Promise.resolve(false);
    }

    // Use custom HTML for radio buttons (Bootstrap 5 Button Group)
    if (input === 'radio') {
        const name = 'swal-radio-options';
        let htmlContent = `<div class="d-flex flex-column mb-1">`;

        if (text) {
            htmlContent += `<p class="text-center mb-3">${text}</p>`;
        }

        // Add custom CSS to make checked state visible
        htmlContent += `
            <style>
                .swal2-html-container .btn-check:checked + .btn-outline-primary {
                    background-color: var(--bs-primary);
                    border-color: var(--bs-primary);
                    color: white;
                }
            </style>
        `;

        htmlContent += `<div class="btn-group w-100" role="group">`;

        const entries = Object.entries(inputOptions);
        entries.forEach(([value, label], index) => {
            const isChecked = inputValue === value || (!inputValue && index === 0) ? 'checked' : '';
            const id = `${name}-${value}`;
            htmlContent += `
                <input type="radio" class="btn-check" name="${name}" id="${id}" value="${value}" ${isChecked} autocomplete="off">
                <label class="btn btn-outline-primary" for="${id}">${label}</label>
            `;
        });

        htmlContent += `</div></div>`;

        const swalOptions: any = {
            title,
            html: htmlContent,
            showCancelButton: true,
            confirmButtonText,
            cancelButtonText,
            reverseButtons: true,
            allowOutsideClick,
            customClass: {
                confirmButton: `btn ${confirmButtonClass}`,
                cancelButton: `btn ${cancelButtonClass}`
            },
            buttonsStyling: false,
            preConfirm: () => {
                const selected = document.querySelector(`input[name="${name}"]:checked`) as HTMLInputElement;
                if (!selected) {
                    Swal.showValidationMessage('You need to choose something!');
                    return false;
                }
                return selected.value;
            }
        };

        return Swal.fire(swalOptions).then((result) => {
            if (result.isConfirmed) {
                return result.value;
            }
            return false;
        });
    }

    // Default behavior for other inputs (like select)
    const swalOptions: any = {
        title,
        text,
        input,
        inputOptions,
        inputValue,
        showCancelButton: true,
        confirmButtonText,
        cancelButtonText,
        reverseButtons: true,
        allowOutsideClick,
        customClass: {
            confirmButton: `btn ${confirmButtonClass}`,
            cancelButton: `btn ${cancelButtonClass}`,
            input: input === 'select' ? 'form-select mx-0 w-100' : undefined
        },
        buttonsStyling: false,
        inputValidator: (value: string) => {
            if (!value) {
                return 'You need to choose something!';
            }
            return null;
        }
    };

    return Swal.fire(swalOptions).then((result) => {
        if (result.isConfirmed) {
            return result.value;
        }
        return false;
    });
}

/**
 * Shows a required text input dialog — validation blocks submission if the field is empty.
 * @param options Configuration options for the dialog
 * @returns Promise that resolves to the trimmed input string if confirmed, false if cancelled
 */
export async function promptDialog(options: PromptDialogOptions): Promise<string | false> {
    const {
        title = 'Input Required',
        description,
        inputPlaceholder = '',
        confirmButtonText = 'Yes',
        cancelButtonText = 'Cancel',
        confirmButtonClass = 'btn-primary',
    } = options;

    const result = await Swal.fire({
        title,
        html: description ?? undefined,
        input: 'textarea',
        inputPlaceholder,
        showCancelButton: true,
        confirmButtonText,
        cancelButtonText,
        reverseButtons: true,
        allowOutsideClick: false,
        customClass: {
            confirmButton: `btn ${confirmButtonClass}`,
            cancelButton: 'btn btn-secondary',
            input: 'form-control mx-0 w-100',
            validationMessage: 'swal-bs-validation',
        },
        buttonsStyling: false,
        inputValidator: (value) => (!value?.trim() ? 'This field is required.' : null),
    });

    return result.isConfirmed ? (result.value as string).trim() : false;
}
